import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, Button, Select, Input, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import CompanyLocationService from "../../services/company-location.service";
import RouteCard from "./RouteCard";
import StoneSummary from "./StoneSummary";

// Task 2186 (F5) -- matches Diamond-PO-Prototype-v3.html's openReturn()/doReturn() (lines
// 1127-1163): the reverse route of a Ship (retailer SHIPPING -> supplier RECEIVING), one
// shipment, tracking required, no per-line decision or margin.

const { Option } = Select;
const CARRIERS = ["Malca-Amit", "Brink's", "FedEx — jewellers' block", "UPS — declared value"];
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toRouteAddress = (loc) =>
  loc && {
    Label: loc.LocationName,
    ContactName: loc.ContactName,
    Address: loc.Address,
    City: loc.City,
    State: loc.State,
    Zip: loc.Zip,
    Country: loc.Country,
    Phone: loc.Phone,
  };

export const ReturnModal = ({ docId, lineIds, onClose, onDone }) => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [fromAddresses, setFromAddresses] = useState([]);
  const [toAddress, setToAddress] = useState(null);
  const [fromAddressId, setFromAddressId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [carrier, setCarrier] = useState(CARRIERS[0]);
  const [trackingNo, setTrackingNo] = useState("");
  const [cost, setCost] = useState("");
  const [billTo, setBillTo] = useState("RETAILER");

  useEffect(() => {
    if (!docId || !dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetDiamondDoc(docId, { DealerID: String(dealerId) })
      .then((docResponse) => {
        if (docResponse.data.status !== true) {
          setLoading(false);
          NotificationManager.error(t(docResponse.data.message || "Could not load this document"));
          return;
        }
        const d = docResponse.data.responseData;
        setDoc(d.Doc);
        setLines((d.Lines || []).filter((l) => lineIds.includes(l.DiamondLineID)));

        Promise.all([
          CompanyLocationService.GetPOLocationDetails({ DealerID: dealerId }),
          CompanyLocationService.GetPOLocationDetails({ DealerID: d.Doc.SupplierDealerID }),
        ]).then(([fromResp, toResp]) => {
          setLoading(false);
          if (fromResp.data.message === "Success" && fromResp.data.responseData?.Table) {
            const rows = fromResp.data.responseData.Table.filter(
              (r) => r.LocationType === "LocationId2" && r.AddressKind === "SHIPPING" && (r.IsActive === true || r.IsActive === "1")
            );
            setFromAddresses(rows);
            const def = rows.find((r) => r.IsDefault === true || r.IsDefault === "1") || rows[0];
            if (def) setFromAddressId(def.LocationId);
          }
          if (toResp.data.message === "Success" && toResp.data.responseData?.Table) {
            const rows = toResp.data.responseData.Table.filter(
              (r) => r.LocationType === "LocationId2" && r.AddressKind === "RECEIVING" && (r.IsActive === true || r.IsActive === "1")
            );
            const def = rows.find((r) => r.IsDefault === true || r.IsDefault === "1") || rows[0] || null;
            setToAddress(def);
          }
        });
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not load this document"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, dealerId]);

  if (!docId) return null;
  if (loading) {
    return (
      <Modal visible onCancel={onClose} footer={null} title={t("Ship return")}>
        <Spin spinning style={{ display: "block", padding: 40 }} />
      </Modal>
    );
  }
  if (!doc || lines.length === 0) return null;

  const fromAddr = fromAddresses.find((a) => a.LocationId === fromAddressId);
  const declaredValue = lines.reduce((sum, l) => sum + Number(l.LineTotal || 0), 0);
  const costNum = Number(cost) || 0;

  const handleSubmit = () => {
    if (!fromAddr || !toAddress) {
      NotificationManager.error(t("Both addresses are required"));
      return;
    }
    if (!trackingNo.trim()) {
      NotificationManager.error(t("Tracking number is required"));
      return;
    }
    setSubmitting(true);
    PurchaseOrderService.ReturnDiamondDoc(docId, {
      DealerID: String(dealerId),
      LineIds: lineIds,
      FromAddressId: String(fromAddressId),
      Carrier: carrier,
      TrackingNo: trackingNo.trim(),
      Cost: costNum,
      BillTo: billTo,
    })
      .then((response) => {
        setSubmitting(false);
        if (response.data.status === true) {
          NotificationManager.success(t("Return shipped — {{tracking}}", { tracking: trackingNo.trim() }));
          onDone && onDone();
          onClose();
        } else {
          NotificationManager.error(t(response.data.message || "Could not ship this return"));
        }
      })
      .catch(() => {
        setSubmitting(false);
        NotificationManager.error(t("Could not ship this return"));
      });
  };

  return (
    <Modal
      visible
      onCancel={onClose}
      width={640}
      title={t("Ship {{n}} stone(s) back to {{supplier}}", { n: lines.length, supplier: doc.SupplierName })}
      footer={[
        <Button key="cancel" onClick={onClose}>{t("Cancel")}</Button>,
        <Button key="go" type="primary" loading={submitting} onClick={handleSubmit}>
          {t("Ship return")}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#6b7689", fontWeight: 700, display: "block", marginBottom: 4 }}>
          {t("Ship from · your shipping address")}
        </label>
        <Select style={{ width: "100%" }} value={fromAddressId} onChange={setFromAddressId}>
          {fromAddresses.map((a) => (
            <Option key={a.LocationId} value={a.LocationId}>
              {a.LocationName} — {a.City}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <RouteCard
          fromAddress={toRouteAddress(fromAddr)}
          toAddress={toRouteAddress(toAddress)}
          fromPartyName={t("You")}
          toPartyName={doc.SupplierName}
        />
      </div>

      <h4 style={{ margin: "0 0 8px" }}>{t("Stones going back")}</h4>
      <div style={{ marginBottom: 14, fontSize: 13 }}>
        {lines.map((l) => {
          const snap = typeof l.StoneSnapshotJson === "string" ? JSON.parse(l.StoneSnapshotJson) : l.StoneSnapshotJson;
          return (
            <div key={l.DiamondLineID} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <StoneSummary snap={snap} />
              <span>{fmtMoney(l.LineTotal)}</span>
            </div>
          );
        })}
      </div>

      <h4 style={{ margin: "0 0 8px" }}>{t("Shipping")}</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr .8fr 1fr", gap: 10, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Carrier")}</label>
          <Select style={{ width: "100%" }} value={carrier} onChange={setCarrier}>
            {CARRIERS.map((c) => (
              <Option key={c} value={c}>{c}</Option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Tracking no.")}</label>
          <Input value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Shipping cost $")}</label>
          <Input value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6b7689", display: "block", marginBottom: 4 }}>{t("Charged to")}</label>
          <Select style={{ width: "100%" }} value={billTo} onChange={setBillTo}>
            <Option value="RETAILER">{t("You")}</Option>
            <Option value="SUPPLIER">{t("Supplier absorbs")}</Option>
          </Select>
        </div>
      </div>

      <div style={{ background: "#eef3ff", border: "1px solid #c9d7fb", borderRadius: 8, padding: 12, fontSize: 13 }}>
        <b>{t("Returning {{n}} stone(s) · declared {{value}}", { n: lines.length, value: fmtMoney(declaredValue) })}</b>
      </div>
    </Modal>
  );
};

export default ReturnModal;
